import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Search, 
  ArrowLeft, 
  ArrowRight, 
  Calendar, 
  Clock, 
  User, 
  Tag,
  Bookmark,
  Heart,
  MessageSquare,
  Share2
} from 'lucide-react';

const Blog = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Sample blog posts data
  const blogPosts = [
    {
      id: 1,
      title: 'İş Takip Sistemleri İle Verimliliğinizi Nasıl Artırabilirsiniz?',
      excerpt: 'Modern iş dünyasında, iş takip sistemleri işletmelerin verimliliğini artırmada kritik rol oynamaktadır. Bu yazıda, Beetrax iş takip sistemi ile işletmenizin verimliliğini nasıl artırabileceğinizi detaylı bir şekilde inceliyoruz.',
      category: 'productivity',
      image: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/blog-1.jpg',
      author: 'Ahmet Yılmaz',
      date: '15 Mayıs 2023',
      readTime: '8 dk',
      featured: true
    },
    {
      id: 2,
      title: 'Stok Yönetiminde Yapılan 5 Yaygın Hata ve Çözümleri',
      excerpt: 'Stok yönetiminde yapılan hatalar, işletmelere ciddi maliyetler çıkarabilir. Bu makalede, en yaygın 5 stok yönetimi hatasını ve Beetrax ile bu hataları nasıl çözebileceğinizi anlatıyoruz.',
      category: 'inventory',
      image: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/blog-2.jpg',
      author: 'Zeynep Demir',
      date: '3 Haziran 2023',
      readTime: '6 dk',
      featured: false
    },
    {
      id: 3,
      title: 'Küçük İşletmeler İçin Dijital Dönüşüm Rehberi',
      excerpt: 'Dijital dönüşüm artık büyük şirketlerin tekelinde değil. Bu rehberde, küçük işletmelerin uygun bütçelerle nasıl dijital dönüşüm gerçekleştirebileceğini ve Beetrax\'ın sunduğu çözümleri keşfedin.',
      category: 'digital-transformation',
      image: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/blog-3.jpg',
      author: 'Emre Şahin',
      date: '17 Temmuz 2023',
      readTime: '10 dk',
      featured: true
    },
    {
      id: 4,
      title: 'Uzaktan Çalışma Döneminde Takım Yönetimi İpuçları',
      excerpt: 'Uzaktan çalışma süreçlerinde ekip yönetimi zorlaşabilir. Bu yazıda, dağınık ekipleri etkili bir şekilde yönetmek için Beetrax araçlarını nasıl kullanabileceğinize dair pratik ipuçları bulacaksınız.',
      category: 'remote-work',
      image: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/blog-4.jpg',
      author: 'Ayşe Yıldız',
      date: '5 Ağustos 2023',
      readTime: '7 dk',
      featured: false
    },
    {
      id: 5,
      title: 'Proje Yönetiminde Çevik (Agile) Metodolojiler ve Beetrax',
      excerpt: 'Çevik (Agile) metodolojiler, modern proje yönetiminin temel taşlarından biridir. Bu makalede, çevik proje yönetimi prensiplerini ve Beetrax\'ın bu prensiplere nasıl uyum sağladığını anlatıyoruz.',
      category: 'project-management',
      image: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/blog-5.jpg',
      author: 'Mehmet Kaya',
      date: '21 Eylül 2023',
      readTime: '9 dk',
      featured: false
    },
    {
      id: 6,
      title: 'İşletmeniz İçin Doğru Yazılım Seçme Rehberi',
      excerpt: 'İşletmeniz için doğru yazılımı seçmek kritik bir karardır. Bu rehberde, ihtiyaçlarınıza en uygun yazılımı seçerken dikkat etmeniz gereken faktörleri ve Beetrax\'ın sunduğu avantajları detaylıca inceliyoruz.',
      category: 'software',
      image: 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/blog-6.jpg',
      author: 'Ahmet Yılmaz',
      date: '10 Ekim 2023',
      readTime: '8 dk',
      featured: false
    },
  ];

  // Blog categories
  const categories = [
    { id: 'all', name: 'Tümü' },
    { id: 'productivity', name: 'Verimlilik' },
    { id: 'inventory', name: 'Stok Yönetimi' },
    { id: 'digital-transformation', name: 'Dijital Dönüşüm' },
    { id: 'remote-work', name: 'Uzaktan Çalışma' },
    { id: 'project-management', name: 'Proje Yönetimi' },
    { id: 'software', name: 'Yazılım' },
  ];

  // Filter posts based on search query and category
  const filteredPosts = blogPosts.filter((post) => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || post.category === activeCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Get featured posts
  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`py-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className={`p-2 rounded-full mr-4 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold">Beetrax Blog</h1>
            </div>
            
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Blog yazısı ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full py-2 pl-10 pr-4 rounded-lg ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                    : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                } border focus:ring-2 focus:ring-blue-500/50 outline-none`}
              />
              <div className="absolute left-3 top-2.5">
                <Search className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <div className="mb-12">
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Öne Çıkan Yazılar
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredPosts.map((post) => (
                <motion.div
                  key={post.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`overflow-hidden rounded-xl ${
                    isDark ? 'bg-gray-800' : 'bg-white'
                  } shadow-md`}
                >
                  <div className="relative h-64">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/blog-placeholder.jpg';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    
                    <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-medium ${
                      isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white'
                    }`}>
                      Öne Çıkan
                    </div>
                    
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-2">{post.title}</h3>
                      <div className="flex items-center text-white/80 text-sm">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>{post.date}</span>
                        <span className="mx-2">•</span>
                        <Clock className="w-4 h-4 mr-1" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-5">
                    <p className={`mb-4 line-clamp-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mr-2 ${
                          isDark ? 'bg-gray-700' : 'bg-gray-200'
                        }`}>
                          <User className="w-4 h-4" />
                        </div>
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {post.author}
                        </span>
                      </div>
                      
                      <button className={`flex items-center text-sm font-medium ${
                        isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                      }`}>
                        <span>Devamını Oku</span>
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* Category Filters */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Tüm Yazılar
            </h2>
            
            <div className={`flex overflow-x-auto space-x-1 p-1 rounded-lg ${
              isDark ? 'bg-gray-800' : 'bg-gray-100'
            }`}>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    activeCategory === category.id
                      ? (isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white')
                      : (isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-200')
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredPosts.map((post) => (
            <motion.div
              key={post.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`overflow-hidden rounded-xl border ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } shadow-sm`}
            >
              <div className="relative h-48">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/blog-placeholder.jpg';
                  }}
                />
                
                <div className={`absolute top-3 right-3 ${
                  isDark ? 'bg-gray-900/70' : 'bg-white/70'
                } backdrop-blur-sm p-1.5 rounded-full`}>
                  <Bookmark className={`w-4 h-4 ${isDark ? 'text-white' : 'text-gray-800'}`} />
                </div>
              </div>
              
              <div className="p-5">
                <div className="flex items-center mb-3">
                  <Tag className={`w-3.5 h-3.5 mr-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {categories.find(c => c.id === post.category)?.name || post.category}
                  </span>
                </div>
                
                <h3 className={`font-bold text-lg mb-2 line-clamp-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {post.title}
                </h3>
                
                <p className={`text-sm mb-4 line-clamp-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between border-t pt-4 mt-3 text-xs">
                  <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <User className="w-3.5 h-3.5 mr-1" />
                    <span>{post.author}</span>
                  </div>
                  
                  <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Calendar className="w-3.5 h-3.5 mr-1" />
                    <span>{post.date}</span>
                  </div>
                  
                  <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Clock className="w-3.5 h-3.5 mr-1" />
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {filteredPosts.length === 0 && (
          <div className={`p-8 text-center rounded-xl ${
            isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <p className={`text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Aramanızla eşleşen blog yazısı bulunamadı. Lütfen farklı bir arama terimi veya kategori deneyin.
            </p>
          </div>
        )}
        
        {/* Newsletter Sign Up */}
        <div className={`rounded-2xl p-8 mb-12 ${
          isDark 
            ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-gray-700' 
            : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-gray-200'
        }`}>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Beetrax Bültenine Abone Olun
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Yeni içerikler, ürün güncellemeleri ve verimlilik ipuçları hakkında düzenli bildirimler alın.
            </p>
            
            <div className="flex max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="E-posta adresiniz"
                className={`flex-grow px-4 py-3 rounded-l-lg ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } border focus:ring-2 focus:ring-blue-500/50 outline-none`}
              />
              <button className={`px-6 py-3 rounded-r-lg font-medium ${
                isDark 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
                Abone Ol
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={`py-8 border-t ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                &copy; {new Date().getFullYear()} Beetrax. Tüm hakları saklıdır.
              </p>
            </div>
            
            <div className="flex space-x-6">
              <Link to="/" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                Ana Sayfa
              </Link>
              <Link to="/contact" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                İletişim
              </Link>
              <Link to="/documentation" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                Dokümantasyon
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog; 